import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlVulnerabilities } from '../../Constants';
import { IAction, SlicePropsWithData } from '../store';

/**
 * Enum for the style in which vulnerabilities should be presented to the user.
 */
export enum VulnerabilityDisplayType {
    Tile,
    Tree,
    List,
}

/**
 * Enum for the type of vulnerability view to show the user.
 */
export enum VulnerabilityViewType {
    Status,
    Type,
    Severity,
    CVSS,
    CVSS_Identifier,
}

/**
 * Initial slice state value.
 */
const initialState: IVulnerabilitiesState = {
    data: [],
    loading: true,
    error: null,
    settings: {
        sidebarOpen: false,
        displayType: VulnerabilityDisplayType.Tile,
        viewType: VulnerabilityViewType.Status,
        openNodes: [],
        activeCategory: 'All',
        filter: '',
        sortAz: true,
        filterVisible: false,
    },
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    /**
     * Updates the active display type.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setDisplayType(state: IVulnerabilitiesState, action: IAction<VulnerabilityDisplayType>) {
        state.settings.displayType = action.payload;
    },

    /**
     * Updates the active view type.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setViewType(state: IVulnerabilitiesState, action: IAction<VulnerabilityViewType>) {
        state.settings.viewType = action.payload;
    },

    /**
     * Updates the active vulnerability category.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setActiveCategory(state: IVulnerabilitiesState, action: IAction<string>) {
        state.settings.activeCategory = action.payload;
    },

    /**
     * Updates the vulnerability filter value.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setFilter(state: IVulnerabilitiesState, action: IAction<string>) {
        state.settings.filter = action.payload;
    },

    /**
     * Updates the vulnerability sorting direction.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setSortAz(state: IVulnerabilitiesState, action: IAction<boolean>) {
        state.settings.sortAz = action.payload;
    },

    /**
     * Updates the vulnerability sidebar open state.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setSidebarOpen(state: IVulnerabilitiesState, action: IAction<boolean>) {
        state.settings.sidebarOpen = action.payload;
    },

    /**
     * Updates the vulnerability filter visibility.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setFilterVisible(state: IVulnerabilitiesState, action: IAction<boolean>) {
        state.settings.filterVisible = action.payload;
    },

    /**
     * Updates the list of open vulnerability TreeView nodes.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setOpenNodes(state: IVulnerabilitiesState, action: IAction<string[]>) {
        state.settings.openNodes = action.payload;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Fetches the vulnerability list from the backend.
     */
    getVulnerabilities: async (): Promise<IEplassVulnerabilityData[]> => {
        try {
            const response = await fetch(urlVulnerabilities);

            return await response.json();
        } catch (e) {
            return Promise.reject(e);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IVulnerabilitiesState>) => {
        /**
         * Reducers for the 'getSettings' thunk.
         */

        builder.addCase(getVulnerabilities.pending, (state: Draft<IVulnerabilitiesState>) => {
            state.loading = true;
        });
        builder.addCase(
            getVulnerabilities.fulfilled,
            (
                state: Draft<IVulnerabilitiesState> ,
                action: IAction<IEplassVulnerabilityData[]>,
            ) => {
                state.loading = false;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getVulnerabilities.rejected,
            (state: Draft<IVulnerabilitiesState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getVulnerabilities = createAsyncThunk('vulnerabilities', thunks.getVulnerabilities);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IVulnerabilitiesState extends SlicePropsWithData<IEplassVulnerabilityData[]> {
    /** User vulnerability settings. */
    settings: IVulnerabilitySettingsState;
}

/**
 * Interface that describes the possible settings for the user's vulnerability data.
 */
export interface IVulnerabilitySettingsState {
    /** If the vulnerabilities sidebar is open or not. */
    sidebarOpen: boolean;

    /** Vulnerability display type (Tree/Tile/Row). */
    displayType: VulnerabilityDisplayType;

    /** Vulnerability view type (All/Favourites/Recent). */
    viewType: VulnerabilityViewType;

    /** User-selected vulnerability category. */
    activeCategory: string;

    /** Collection of open TreeView nodes. */
    openNodes: string[];

    /** User vulnerability name filter. */
    filter: string;

    /** Vulnerability sorting direction. */
    sortAz: boolean;

    /** If the filter input is visible or not. */
    filterVisible: boolean;
}

/**
 * Interface that describes the properties of an Eplass Vulnerability.
 */
export interface IEplassVulnerabilityData {
    /** Vulnerability ticketId. */
    ticketId: string;

    /** Vulnerability status. */
    status: string;

    /** Vulnerability description. */
    description: string;

    /** Vulnerability pusblished date */
    published: string;

    /** Vulnerability Type. */
    type: string;

    /** Vulnerability product. */
    product: string;

    /** Vulnerability severity grade. */
    severity: string;

    /** Vulnerability impact factor. */
    impact: string;

    /** Vulnerability cvss score. */
    cvss?: string;

    /** Vulnerability cvss identifier. */
    cvss_identifier?: string;

    /** Vulnerability updated date. */
    updated?: string;
}

/**
 * Create the redux slice.
 */
export const vulnerabilitiesSlice = createSlice({
    name: 'vulnerabilities',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const {
    setDisplayType,
    setViewType,
    setActiveCategory,
    setFilter,
    setSortAz,
    setFilterVisible,
    setSidebarOpen,
    setOpenNodes,
} = vulnerabilitiesSlice.actions;

/**
 * Export slice reducer as default.
 */
export default vulnerabilitiesSlice.reducer;
